import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, Download } from "lucide-react";
import { API, BACKEND_URL } from "@/constants/content";
import { FORM } from "@/constants/testIds";

// Convert relative "/api/..." URLs to absolute so downloads work in new tabs
function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
}

/**
 * Reusable subscribe form bound to a single magnet key.
 * Renders inline (no modal). Parent decides where to place it.
 *
 * Props:
 *  - magnet: "sport" | "suli" | "aroma" | "general"
 *  - magnetLabel: optional override
 *  - compact: smaller layout when true
 *  - onSuccess: optional callback
 */
export default function SubscribeForm({ magnet = "general", magnetLabel, compact = false, onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { name, email, ebook_url, magnet_label }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Kérjük, fogadd el az adatkezelési hozzájárulást.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        name: name.trim(),
        email: email.trim(),
        consent,
        magnet,
        source: "mozgas-ejszakaja",
      });
      setResult(data);
      toast.success("Köszönjük! Az ajándékod letöltési gombja megjelent.");
      onSuccess?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Hiba történt. Próbáld újra kicsit később.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const downloads = Array.isArray(result.downloads) && result.downloads.length
      ? result.downloads.map((d) => ({ ...d, url: resolveUrl(d.url) }))
      : (result.ebook_url ? [{ label: "Letöltöm az anyagot", url: resolveUrl(result.ebook_url) }] : []);
    return (
      <motion.div
        data-testid={FORM.success}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="inline-flex h-14 w-14 rounded-full bg-[#2A9D8F]/10 border border-[#2A9D8F]/30 items-center justify-center mb-5">
          <Check className="h-6 w-6 text-[#2A9D8F]" />
        </div>
        <div className="font-serif-display text-2xl md:text-3xl text-[#1A2825]">
          Köszönjük, {result.name || "kedves"}!
        </div>
        <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed max-w-md mx-auto">
          Felvettünk a <span className="font-medium text-[#1A2825]">{result.magnet_label || magnetLabel || magnet}</span>
          {" "}hírlevélbe. Tessék, {downloads.length > 1 ? "itt az ajándékaid:" : "itt az ajándékod:"}
        </p>
        {downloads.length > 0 ? (
          <div className="mt-6 flex flex-col gap-3 items-center">
            {downloads.map((d, i) => (
              <a
                key={d.url}
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={i === 0 ? FORM.ebookDownload : `${FORM.ebookDownload}-${i}`}
                className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-0.5 ${
                  i === 0
                    ? "bg-[#2A9D8F] hover:bg-[#1C6B61] text-white"
                    : "bg-white border border-[#2A9D8F]/30 text-[#1C6B61] hover:border-[#2A9D8F]"
                }`}
              >
                <Download className="h-4 w-4" />
                {d.label || `Letöltés ${i + 1}`}
              </a>
            ))}
          </div>
        ) : (
          <p className="mt-6 text-sm text-[#A88A2B]">
            Az anyagot rövidesen emailben küldjük a {result.email} címre.
          </p>
        )}
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Keresztneved</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid={FORM.name}
          placeholder="pl. Anna"
          className="mt-1.5 w-full bg-white border border-[#EAE5D9] focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none rounded-2xl px-5 py-3 text-[#1A2825] placeholder:text-[#B5C0BE] transition-colors"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-[#758C88]">Email címed</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={FORM.email}
          placeholder="te@email.hu"
          className="mt-1.5 w-full bg-white border border-[#EAE5D9] focus:border-[#2A9D8F] focus:ring-1 focus:ring-[#2A9D8F] outline-none rounded-2xl px-5 py-3 text-[#1A2825] placeholder:text-[#B5C0BE] transition-colors"
        />
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid={FORM.consent}
          className="mt-1 appearance-none w-5 h-5 border border-[#2A9D8F] rounded-md checked:bg-[#2A9D8F] checked:border-transparent focus:ring-2 focus:ring-[#2A9D8F]/20 cursor-pointer relative
            checked:after:content-['✓'] checked:after:text-white checked:after:text-xs checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center"
        />
        <span className="text-xs text-[#3A4D4A] font-light leading-relaxed">
          Hozzájárulok, hogy a TestLélekHarmónia időről időre emailes tartalmakat
          küldjön nekem (4Life ajánlók, masszázs tippek). Bármikor leiratkozhatok.
          Részletek az{" "}
          <a
            href="/adatkezeles"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1C6B61] underline hover:text-[#2A9D8F]"
          >
            Adatkezelési tájékoztatóban
          </a>
          .
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid={FORM.submit}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#1C6B61] disabled:opacity-60 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)] hover:-translate-y-0.5"
      >
        {loading ? "Feliratkozás..." : "Kérem az ajándékot"}
        {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
      {!compact && (
        <p className="text-center text-[11px] text-[#758C88] tracking-wide">
          Soha nem spammelünk. Csak releváns tartalmakat küldünk.
        </p>
      )}
    </form>
  );
}
